import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';

/**
 * Quality-scoring domain (fox F10 + F11) — the ruleset_v1 Quality Check
 * payload plus the F11 universe layer (percentiles/factors, peer group,
 * fair value, seasonal cycle, total returns).
 * Key shapes carry every output-affecting parameter (asOf, peer mode).
 */
export function createScoringQueries(client: StoxlyClient) {
  return createQueryKeys('scoring', {
    // ['scoring', 'quality', symbol, { asOf }]
    quality: (symbol: string, asOf?: string) => ({
      queryKey: [symbol, { asOf: asOf ?? null }],
      queryFn: ({ signal }) =>
        client.scoringGetQualityScoring({
          params: { symbol },
          queries: asOf ? { asOf } : undefined,
          signal,
        }),
    }),
    // ['scoring', 'universeContext', symbol]
    universeContext: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.scoringGetUniverseContext({ params: { symbol }, signal }),
    }),
    // ['scoring', 'peerGroup', symbol, { mode, watchlistId }]
    peerGroup: (
      symbol: string,
      mode: 'industry' | 'country' | 'cap' | 'list' = 'industry',
      watchlistId?: string,
    ) => ({
      queryKey: [symbol, { mode, watchlistId: watchlistId ?? null }],
      queryFn: ({ signal }) =>
        client.scoringGetPeerGroup({
          params: { symbol },
          queries: { mode, ...(watchlistId ? { watchlistId } : {}) },
          signal,
        }),
    }),
    // ['scoring', 'fairValue', symbol]
    fairValue: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.scoringGetFairValue({ params: { symbol }, signal }),
    }),
    // ['scoring', 'seasonalCycle', symbol]
    seasonalCycle: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.scoringGetSeasonalCycle({ params: { symbol }, signal }),
    }),
    // ['scoring', 'totalReturns', symbol]
    totalReturns: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.scoringGetTotalReturns({ params: { symbol }, signal }),
    }),
    // ['scoring', 'backtestSummary'] — fox F13. The active ruleset's walk-forward
    // evidence (IC, quantile spread, the survivorship caveat verbatim). One
    // answer for the whole universe, so the key carries no parameter.
    backtestSummary: {
      queryKey: null,
      queryFn: ({ signal }) => client.scoringBacktestGetSummary({ signal }),
    },
    // ['scoring', 'backtestReplay', symbol] — the per-company factual replay
    // (score as of 1/3/5/10y ago vs the stock and its benchmark since); the
    // survivorship caveat + disclaimer ride in the payload and MUST render.
    backtestReplay: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) => client.scoringBacktestGetReplay({ params: { symbol }, signal }),
    }),
  });
}
